import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    x: ['M18 6L6 18', 'M6 6l12 12'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    copy: ['M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H8z', 'M8 4V2a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2'],
};

const ACTION_CONFIG = {
    'Operator Onboarding': {
        title: 'Operator Onboarding Welcome',
        subject: 'Welcome to {company_name} - Complete your onboarding',
        body: `<p>Hi {operator_name},</p>
<p>Welcome to <strong>{company_name}</strong>! We're thrilled to have you on board.</p>
<p>This is a reminder to complete your onboarding process. Your account has been successfully set up and you're ready to get started. Here are a few things to help you begin:</p>
<ul>
  <li>Log in to your operator dashboard</li>
  <li>Complete your profile and company details</li>
  <li>Explore your subscription plan features</li>
</ul>
<p>If you have any questions or need assistance, don't hesitate to reach out to us.</p>
<p>Kind Regards,<br/>{company_name} Team</p>`
    },
    'Price Plan Remainder': {
        title: 'Subscription Expiry Reminder',
        subject: 'Action Required: Your subscription is expiring soon',
        body: `<p>Hi {operator_name},</p>
<p>This is a friendly reminder that your current subscription plan is approaching its expiry date.</p>
<p><strong>Subscription Plan:</strong> {subscription_plan}<br/>
<strong>Operator ID:</strong> {operator_id}</p>
<p>To avoid any interruption to your services, please renew your plan before it expires. You can manage your subscription from your operator portal.</p>
<p>If you need help choosing the right plan or have any questions, feel free to contact us.</p>
<p>Kind Regards,<br/>{company_name} Team</p>`
    },
    'Notice': {
        title: 'System Update Notice',
        subject: 'Important Notice: Updated terms and conditions',
        body: `<p>Hi {operator_name},</p>
<p>We would like to inform you of an important update regarding your operator account.</p>
<p>Please review the information below and take any necessary action at your earliest convenience.</p>
<p>If you have questions or require further clarification, please reach out to our support team.</p>
<p>Kind Regards,<br/>{company_name} Team</p>`
    }
};

const ACTION_TYPES = Object.keys(ACTION_CONFIG);

/* ── Merge Fields ── */
const MERGE_FIELDS = {
    'Operator': [
        { label: 'Operator Name', tag: '{operator_name}' },
        { label: 'Operator Email', tag: '{operator_email}' },
        { label: 'Operator Phone', tag: '{operator_phone}' },
        { label: 'Company Name', tag: '{company_name}' },
        { label: 'Operator ID', tag: '{operator_id}' },
        { label: 'Operator Status', tag: '{operator_status}' },
        { label: 'Operator City', tag: '{operator_city}' },
        { label: 'Operator Country', tag: '{operator_country}' },
        { label: 'Operator Address', tag: '{operator_address}' },
        { label: 'Subscription Plan', tag: '{subscription_plan}' },
    ],
    'System': [
        { label: 'CRM URL', tag: '{crm_url}' },
        { label: 'Admin URL', tag: '{admin_url}' },
        { label: 'Main Domain', tag: '{main_domain}' },
        { label: 'Logo URL', tag: '{logo_url}' },
        { label: 'Email Signature', tag: '{email_signature}' },
    ],
};

/* ── Toolbar ── */
function RichToolbar({ editorRef }) {
    const exec = (cmd, val = null) => { editorRef.current?.focus(); document.execCommand(cmd, false, val); };
    return (
        <div className="et-toolbar">
            <button type="button" className="et-tb-btn" onClick={() => exec('bold')}><b>B</b></button>
            <button type="button" className="et-tb-btn" onClick={() => exec('italic')}><i>I</i></button>
            <button type="button" className="et-tb-btn" onClick={() => exec('underline')}><u>U</u></button>
            <span className="et-toolbar-divider" />
            <button type="button" className="et-tb-btn" onClick={() => exec('insertUnorderedList')}>• List</button>
            <button type="button" className="et-tb-btn" onClick={() => exec('removeFormat')}>Tx</button>
        </div>
    );
}

/* ── Merge Fields Panel ── */
function MergeFieldsPanel({ editorRef }) {
    const [copied, setCopied] = useState(null);

    const insertTag = (tag) => {
        editorRef.current?.focus();
        document.execCommand('insertText', false, tag);
        setCopied(tag);
        setTimeout(() => setCopied(null), 1200);
    };

    return (
        <div className="et-merge-panel">
            <div className="et-merge-panel-header">
                <span className="et-merge-panel-title">Available merge fields</span>
                <p className="et-merge-panel-hint">Click a field to insert it into the email body</p>
            </div>
            <div className="et-merge-panel-body">
                {Object.entries(MERGE_FIELDS).map(([section, fields]) => (
                    <div key={section} className="et-merge-section">
                        <div className="et-merge-section-label">{section}</div>
                        <div className="et-merge-table">
                            {fields.map(({ label, tag }) => (
                                <div key={tag} className="et-merge-row" onClick={() => insertTag(tag)}>
                                    <span className="et-merge-field-label">{label}</span>
                                    <span className={`et-merge-tag${copied === tag ? ' copied' : ''}`}>
                                        {copied === tag ? '✓ Copied' : tag}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Template Modal ── */
function TemplateModal({ existing, onClose, onSaved }) {
    const isEdit = !!existing;
    const [form, setForm] = useState({
        name: existing?.name || '',
        action: existing?.action || '',
        subject: existing?.subject || '',
        fromName: existing?.fromName || '{company_name} | CRM',
        active: existing ? existing.active : true,
    });
    const [saving, setSaving] = useState(false);
    const editorRef = useRef(null);

    useEffect(() => {
        if (existing?.body && editorRef.current) {
            editorRef.current.innerHTML = existing.body;
        }
    }, [existing]);

    const handleActionChange = (action) => {
        const config = ACTION_CONFIG[action];
        if (!config) return;

        const currentBody = editorRef.current?.innerHTML || '';
        const isEmptyBody = !currentBody || currentBody === '<br>' || currentBody === '<div><br></div>';
        const isDefaultTitle = !form.name || form.name === (ACTION_CONFIG[form.action]?.title || '');
        const isDefaultSubject = !form.subject || form.subject === (ACTION_CONFIG[form.action]?.subject || '');

        const shouldUpdate = isEmptyBody || window.confirm(`Apply default ${action} template settings? This will update Title, Subject, and Body.`);

        if (shouldUpdate) {
            setForm(f => ({
                ...f,
                action,
                name: config.title,
                subject: config.subject
            }));
            if (editorRef.current) editorRef.current.innerHTML = config.body;
        } else {
            // Just update the action pill selection without changing content
            setForm(f => ({ ...f, action }));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        const body = editorRef.current?.innerHTML || '';
        const payload = { ...form, body };
        try {
            const url = isEdit ? `${API}/email-templates/${existing._id}` : `${API}/email-templates`;
            const method = isEdit ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            onSaved(data.template || data, isEdit);
            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="et-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="et-modal-wide">
                {/* Header */}
                <div className="et-modal-header">
                    <h2 className="et-modal-title">{isEdit ? 'Edit Template' : 'New Email Template'}</h2>
                    <button className="et-modal-close" onClick={onClose}><Icon d={icons.x} /></button>
                </div>

                {/* Two-column body */}
                <div className="et-modal-cols">
                    {/* LEFT – Form */}
                    <div className="et-modal-left">
                        {/* Template Name */}
                        <div className="et-form-group">
                            <label className="et-label">* Template Title</label>
                            <input
                                className="et-input"
                                placeholder="e.g. New Subscription Welcome"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                            />
                        </div>

                        {/* Subject */}
                        <div className="et-form-group">
                            <label className="et-label">Subject</label>
                            <input
                                className="et-input"
                                placeholder="Email subject line"
                                value={form.subject}
                                onChange={e => setForm({ ...form, subject: e.target.value })}
                            />
                        </div>

                        {/* From Name */}
                        <div className="et-form-group">
                            <label className="et-label">* From Name</label>
                            <input
                                className="et-input"
                                placeholder="{company_name} | CRM"
                                value={form.fromName}
                                onChange={e => setForm({ ...form, fromName: e.target.value })}
                            />
                        </div>

                        {/* Action */}
                        <div className="et-form-group">
                            <label className="et-label">Action</label>
                            <select
                                className="et-input"
                                value={form.action}
                                onChange={e => handleActionChange(e.target.value)}
                            >
                                <option value="" disabled>Select an action...</option>
                                {ACTION_TYPES.map(a => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>

                        {/* Active toggle */}
                        <div className="et-toggle-row">
                            <label className="et-label" style={{ marginBottom: 0 }}>Status</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <button
                                    type="button"
                                    className={`et-toggle${form.active ? ' on' : ''}`}
                                    onClick={() => setForm({ ...form, active: !form.active })}
                                >
                                    <span className="et-toggle-thumb" />
                                </button>
                                <span className="et-toggle-label">{form.active ? 'Active' : 'Disabled'}</span>
                            </div>
                        </div>

                        {/* Rich text editor */}
                        <div className="et-form-group">
                            <label className="et-label">Email Body</label>
                            <div className="et-editor-wrap">
                                <RichToolbar editorRef={editorRef} />
                                <div
                                    ref={editorRef}
                                    className="et-editor"
                                    contentEditable
                                    suppressContentEditableWarning
                                    data-placeholder="Type your email body here, or click a merge field on the right to insert it..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* RIGHT – Merge fields */}
                    <div className="et-modal-right">
                        <MergeFieldsPanel editorRef={editorRef} />
                    </div>
                </div>

                {/* Footer */}
                <div className="et-modal-footer">
                    <button className="et-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="et-btn-save" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : isEdit ? 'Update Template' : 'Save Template'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Main Component ── */
export default function EmailTemplates() {
    const [templates, setTemplates] = useState([]);
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { fetchTemplates(); }, []);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/email-templates`);
            setTemplates(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const onSaved = (item, isEdit) => {
        if (isEdit) setTemplates(prev => prev.map(t => t._id === item._id ? item : t));
        else setTemplates(prev => [item, ...prev]);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this template?')) return;
        await fetch(`${API}/email-templates/${id}`, { method: 'DELETE' });
        setTemplates(prev => prev.filter(t => t._id !== id));
    };

    const filtered = templates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="et-page">
            <div className="et-header">
                <div>
                    <h1 className="et-title">Email Templates</h1>
                    <p className="et-subtitle">Manage and customize your system email communications.</p>
                </div>
                <div className="et-actions">
                    <div className="et-search-box">
                        <Icon d={icons.search} size={14} />
                        <input placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    <button className="et-add-btn" onClick={() => { setEditItem(null); setShowModal(true); }}>
                        <Icon d={icons.plus} size={14} /> Add Template
                    </button>
                </div>
            </div>

            <div className="et-content">
                {loading ? (
                    <div className="et-loading">Loading templates...</div>
                ) : filtered.length === 0 ? (
                    <div className="et-empty-state">No templates found.</div>
                ) : (
                    <div className="et-list-container">
                        {filtered.map(t => (
                            <div key={t._id} className={`et-list-item et-action-${(t.action || 'general').toLowerCase().replace(/\s+/g, '-')}`}>
                                <div className="et-li-info">
                                    <h3 className="et-li-name">{t.name}</h3>
                                    <p className="et-li-sub">{t.subject}</p>
                                </div>
                                <div className="et-li-meta">
                                    {t.action && <span className={`et-li-badge et-action-badge-${t.action.toLowerCase().replace(/\s+/g, '-')}`}>{t.action}</span>}
                                    <span className={`et-li-status ${t.active ? 'active' : 'inactive'}`}>
                                        {t.active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="et-li-actions">
                                    <button className="et-li-btn edit" onClick={() => { setEditItem(t); setShowModal(true); }} title="Edit">
                                        <Icon d={icons.edit} size={16} />
                                    </button>
                                    <button className="et-li-btn delete" onClick={() => handleDelete(t._id)} title="Delete">
                                        <Icon d={icons.trash} size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showModal && (
                <TemplateModal
                    existing={editItem}
                    onClose={() => { setShowModal(false); setEditItem(null); }}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}
